import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CtaTopScribble, CtaUnderlineScribble } from "@/components/site/VectorMarks";


export type CTASectionProps = {
  title: React.ReactNode;
  description?: string;
  useCase: string;
  buttonIcon?: React.ReactNode;
  showUnderline?: boolean;
};

const ctaSchema = z.object({
  email: z.string().trim().email("Please enter a valid email").max(255),
  message: z.string().trim().min(1, "Message is required").max(1000),
});

export default function CTASection({
  title,
  description,
  useCase,
  buttonIcon,
  showUnderline = true,
}: CTASectionProps) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = ctaSchema.safeParse({ email, message });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const { error } = await supabase.from("leads").insert({
        name: "",
        company: null,
        email,
        use_case: useCase,
        message,
      });
      if (error) throw error;
      toast({
        title: "Thank you!",
        description: "We'll get back to you within 24 hours.",
      });
      setEmail("");
      setMessage("");
    } catch {
      toast({
        title: "Couldn't send message",
        description: "Please try again or email info@yootelco.com directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const baseId = useCase.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <section className="section-shell">
      <div className="cta-shell relative isolate overflow-hidden rounded-[36px] border border-border/70 px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
        <div className="cta-grid-overlay absolute inset-0" aria-hidden="true" />
        <CtaTopScribble className="absolute right-5 top-4 z-0 h-auto w-[108px] opacity-80 md:right-8 md:top-6 md:w-[126px]" />
        <div className="relative z-10">
          <h2 className="section-title">{title}</h2>
          {description && (
            <div className="mx-auto mt-5 flex max-w-2xl flex-col items-center">
              <p className="text-lg text-muted-foreground">{description}</p>
              {showUnderline && <CtaUnderlineScribble className="mt-2 h-auto w-[92px]" />}
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            className="mx-auto mt-8 flex max-w-[520px] flex-col gap-4 text-left"
            noValidate
          >
            <div>
              <label htmlFor={`cta-email-${baseId}`} className="sr-only">
                Email
              </label>
              <input
                id={`cta-email-${baseId}`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                maxLength={255}
                className="h-12 w-full rounded-[8px] border border-border bg-background px-4 text-[14px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              {errors.email && (
                <p className="mt-1 text-[12px] text-destructive">{errors.email}</p>
              )}
            </div>
            <div>
              <label htmlFor={`cta-message-${baseId}`} className="sr-only">
                Message
              </label>
              <textarea
                id={`cta-message-${baseId}`}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What are you looking to send, and roughly how many messages/month?"
                rows={3}
                maxLength={1000}
                className="w-full resize-y rounded-[8px] border border-border bg-background px-4 py-3 text-[14px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              {errors.message && (
                <p className="mt-1 text-[12px] text-destructive">{errors.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-8 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-70"
            >
              {buttonIcon}
              Contact Sales
            </button>
          </form>
          <p className="mt-4 text-sm text-muted-foreground">
            or{" "}
            <Link to="/contact" className="underline hover:text-foreground">
              fill out the full form
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
